import { imgixHost } from 'src/config'
import Discount from 'src/models/discount/Discount'
import GiftCard from 'src/models/gift-card/GiftCard'
import { SALES_ITEM_RESTRICTION_TYPES } from 'src/models/inventory-strategy/InventoryStrategy'
import Item from 'src/models/item/Item'
import ItemVariation from 'src/models/item/ItemVariation'
import Model, { ID, Integer, JsonObject } from 'src/models/Model'
import ItemOrderItemModifier from 'src/models/order-item/ItemOrderItemModifier'
import OrderItem from 'src/models/order-item/OrderItem'
import ItemPackage from 'src/models/package/ItemPackage'
import Voucher from 'src/models/voucher/Voucher'
import VoucherPackage from 'src/models/voucher/VoucherPackage'
import VoucherType from 'src/models/voucher/VoucherType'

export const BUTTON_TYPES = {
  APPLY_VOUCHER: 'apply-voucher',
  CASH_OUT: 'cash-out',
  DISCOUNT: 'discount',
  DONATION: 'donation',
  GIFT_CARD: 'gift-card',
  ITEM: 'item',
  ITEM_PACKAGE: 'item-package',
  ITEM_VARIATION: 'item-variation',
  MISC_ITEM: 'misc-item',
  USER_CREDIT: 'user-credit',
  VOUCHER_PACKAGE: 'voucher-package',
  VOUCHER_SALE: 'voucher-sale',
  VOUCHER_SERVICE_ISSUE: 'voucher-service-issue',
  MANUAL_DISCOUNT: 'manual-discount'
}

BUTTON_TYPES.ALL = _.values(BUTTON_TYPES)

export default class RegisterButton extends Model {
  static name = 'RegisterButton'

  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.HardwareAndRegisterStation]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      type: {
        type: String,
        options: BUTTON_TYPES.ALL,
        default: BUTTON_TYPES.ITEM
      },
      overrideSkipDetailDialog: {
        type: Boolean,
        default: false
      },
      skipDetailDialog: {
        type: Boolean,
        default: false
      },
      item: {
        type: Item,
        reference: true,
        default: null
      },
      itemVariation: {
        type: ItemVariation,
        reference: true,
        default: null
      },
      voucherType: {
        type: VoucherType,
        reference: true
      },
      voucherPackage: {
        type: VoucherPackage,
        default: null,
        reference: true
      },
      discount: {
        type: Discount,
        reference: true
      },
      discounts: {
        type: [Discount],
        default: () => { return [] },
        encode (data) {
          data.discountIds = _.map(this.discounts.data, 'id')
        },
        reference: true
      },
      itemPackage: {
        type: ItemPackage,
        default: null,
        reference: true
      },
      overrideImage: {
        type: Boolean,
        default: false
      },
      image: {
        type: String,
        default: null
      },
      imageParams: {
        type: String,
        default: 'w=190&h=118&fit=crop&fill=solid&fill-color=5b5b5b&bg=5b5b5b&auto=format',
        local: true
      },
      buttonOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            button_name: null,
            default_description: '',
            default_amount: 0,
            default_taxable: false,
            default_tax_ids: [],
            force_tax: false,
            default_print: false,
            default_print_location_id: null,
            donor_perfect_campaign_code: null,
            requires_authorization: false
          }
        }
      }
    }

    this.computed = {
      imageUrl () {
        return this.resolvedImageUrl()
      },
      salesDisabled () {
        switch (this.type) {
          case 'item':
            return this.item?.salesUnavailable || false
          case 'item-variation':
            return this.itemVariation?.salesDisabled || false
          default:
            return false
        }
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      }
    }
  }

  availableStock ({
    deliveryType,
    itemVariationAvailabilitiesAndConstraints,
    modifierAvailabilitiesAndConstraints,
    screen
  }) {
    switch (this.type) {
      case 'item':
        return this.item.availableStock({
          deliveryType,
          itemVariationAvailabilitiesAndConstraints,
          modifierAvailabilitiesAndConstraints,
          screen
        })
      case 'item-variation':
        return this.itemVariation.availableStock({
          availabilitiesAndConstraints: itemVariationAvailabilitiesAndConstraints,
          deliveryType,
          screen
        })
      default:
        return null
    }
  }

  isOutOfStock ({
    deliveryType,
    itemVariationAvailabilitiesAndConstraints,
    modifierAvailabilitiesAndConstraints,
    screen
  }) {
    switch (this.type) {
      case 'item':
        return this.item.isOutOfStock({
          deliveryType,
          itemVariationAvailabilitiesAndConstraints,
          modifierAvailabilitiesAndConstraints,
          screen
        })
      case 'item-variation':
        return this.itemVariation.isOutOfStock({
          availabilitiesAndConstraints: itemVariationAvailabilitiesAndConstraints,
          deliveryType,
          screen
        })
      default:
        return false
    }
  }

  resolvedImageUrl () {
    if (this.overrideImage && this.image) return `${imgixHost}/${this.image}?${this.imageParams}`
    const site = this.configuration.currentSite
    switch (this.type) {
      case 'item':
        return this.item?.imageUrl
      case 'item-variation':
        return this.itemVariation?.imageUrl || this.resolvedItem?.imageUrl
      case 'item-package':
        return this.itemPackage?.imageUrl
      case 'discount':
        return site.discountImageUrl
      case 'voucher-sale':
        return this.voucherType?.imageUrl || site.voucherSaleImageUrl
      case 'voucher-package':
        return this.voucherPackage?.imageUrl
      case 'voucher-service-issue':
        return site.voucherServiceIssueImageUrl
      case 'gift-card':
        return site.giftCardImageUrl
      case 'misc-item':
        return site.miscItemImageUrl
      case 'cash-out':
        return site.cashOutItemImageUrl
      case 'user-credit':
        return site.userCreditImageUrl
      case 'apply-voucher':
        return site.applyVoucherImageUrl

      default:
        return ''
    }
  }

  get name () {
    switch (this.type) {
      case BUTTON_TYPES.ITEM:
        return this.resolvedItem?.displayName

      case BUTTON_TYPES.ITEM_VARIATION:
        return this.itemVariation?.name

      case BUTTON_TYPES.ITEM_PACKAGE:
        return this.itemPackage?.name

      case BUTTON_TYPES.VOUCHER_SALE:
      case BUTTON_TYPES.VOUCHER_SERVICE_ISSUE:
        return this.voucherType?.name

      case BUTTON_TYPES.VOUCHER_PACKAGE:
        return this.voucherPackage?.name

      case BUTTON_TYPES.DISCOUNT:
        return this.discount?.displayName

      case BUTTON_TYPES.APPLY_VOUCHER:
        return this.voucherType?.name

      case BUTTON_TYPES.GIFT_CARD:
        return 'Gift'

      case BUTTON_TYPES.MISC_ITEM:
        return this.buttonOptions.button_name || 'Miscellaneous'

      case BUTTON_TYPES.DONATION:
        return this.buttonOptions.button_name

      case BUTTON_TYPES.CASH_OUT:
        return 'Cash Out'

      case BUTTON_TYPES.USER_CREDIT:
        return 'User Credit'

      case BUTTON_TYPES.MANUAL_DISCOUNT:
        return this.buttonOptions.button_name || (this.discounts?.data?.length > 0 ? this.discounts.data[0].displayName + ' (Manual)' : 'Manual Discount')

      default:
        throw new Error('Unhandled button type: ' + this.type)
    }
  }

  get showAlcoholAcknowledgement () {
    if (this.type !== BUTTON_TYPES.ITEM) return false

    return !!this.item?.category && this.item.category.hasAlcoholAcknowledgement && !this.item.ignoreAlcoholAcknowledgement
  }

  get itemIgnoreAlcoholAcknowledgement () {
    if (this.type !== BUTTON_TYPES.ITEM) return false

    if (!this.item.category || !this.item.category.hasAlcoholAcknowledgement) return false

    return this.item.ignoreAlcoholAcknowledgement
  }

  get finalSkipDetailDialog () {
    if (this.type !== BUTTON_TYPES.ITEM && this.type !== BUTTON_TYPES.ITEM_VARIATION) return true

    return this.overrideSkipDetailDialog ? this.skipDetailDialog : this.resolvedItem?.skipDetailDialog
  }

  get resolvedItem () {
    if (this.type === BUTTON_TYPES.ITEM_VARIATION) {
      return this.itemVariation?.item || this.item
    } else {
      return this.item
    }
  }

  get abbreviation () {
    switch (this.type) {
      case BUTTON_TYPES.ITEM:
        return this.resolvedItem?.abbreviation

      case BUTTON_TYPES.ITEM_VARIATION:
        return this.resolvedItem?.abbreviation

      case BUTTON_TYPES.ITEM_PACKAGE:
        return 'Pack'

      case BUTTON_TYPES.VOUCHER_SALE:
        return 'Vouc'

      case BUTTON_TYPES.VOUCHER_SERVICE_ISSUE:
        return 'Serv'

      case BUTTON_TYPES.VOUCHER_PACKAGE:
        return 'Vpak'

      case BUTTON_TYPES.DISCOUNT:
        return 'Disc'

      case BUTTON_TYPES.APPLY_VOUCHER:
        return 'Vouc'

      case BUTTON_TYPES.GIFT_CARD:
        return 'Gift'

      case BUTTON_TYPES.MISC_ITEM:
        return 'Misc'

      case BUTTON_TYPES.DONATION:
        return 'Dona'

      case BUTTON_TYPES.CASH_OUT:
        return 'Cash'

      case BUTTON_TYPES.USER_CREDIT:
        return 'Cred'

      case BUTTON_TYPES.MANUAL_DISCOUNT:
        return 'MDsc'

      default:
        throw new Error('Unhandled button type: ' + this.type)
    }
  }

  get backgroundColor () {
    switch (this.type) {
      case BUTTON_TYPES.ITEM:
        return this.resolvedItem?.color

      case BUTTON_TYPES.ITEM_VARIATION:
        return this.resolvedItem?.color

      case BUTTON_TYPES.ITEM_PACKAGE:
        return this.itemPackage.color

      case BUTTON_TYPES.VOUCHER_SALE:
        return '#94C'

      case BUTTON_TYPES.VOUCHER_SERVICE_ISSUE:
        return '#949'

      case BUTTON_TYPES.VOUCHER_PACKAGE:
        return '#94C'

      case BUTTON_TYPES.DISCOUNT:
        return '#94C'

      case BUTTON_TYPES.APPLY_VOUCHER:
        return '#94C'

      case BUTTON_TYPES.GIFT_CARD:
        return '#34C'

      case BUTTON_TYPES.MISC_ITEM:
        return '#44C'

      case BUTTON_TYPES.DONATION:
        return '#49C'

      case BUTTON_TYPES.CASH_OUT:
        return '#84C'

      case BUTTON_TYPES.USER_CREDIT:
        return '#4C4'

      case BUTTON_TYPES.MANUAL_DISCOUNT:
        return '#949'

      default:
        throw new Error('Unhandled button type: ' + this.type)
    }
  }

  // TODO, don't overwrite price here!! And don't overload meaning of price vs. amount
  get price () {
    return this.itemVariation.price
  }

  get requiresAuthorization () {
    return this.buttonOptions.requires_authorization
  }

  orderItemForButton (options) {
    if (this.type === BUTTON_TYPES.ITEM || this.type === BUTTON_TYPES.ITEM_VARIATION) {
      return this.itemOrderItemForButton(options)
    } else if (this.type === BUTTON_TYPES.VOUCHER_SALE) {
      return this.voucherOrderItemForButton()
    } else if (this.type === BUTTON_TYPES.VOUCHER_SERVICE_ISSUE) {
      return this.voucherOrderItemForButton(true)
    } else if (this.type === BUTTON_TYPES.GIFT_CARD) {
      return this.giftCardOrderItemForButton()
    } else if (this.type === BUTTON_TYPES.MISC_ITEM) {
      return this.miscOrderItemForButton()
    } else if (this.type === BUTTON_TYPES.DONATION) {
      return this.donationOrderItemForButton()
    } else if (this.type === BUTTON_TYPES.CASH_OUT) {
      return this.miscCashOutOrderItemForButton()
    } else if (this.type === BUTTON_TYPES.USER_CREDIT) {
      return this.giftCardOrderItemForButton()
    } else if (this.type === BUTTON_TYPES.ITEM_PACKAGE) {
      return this.itemPackageForButton()
    } else if (this.type === BUTTON_TYPES.VOUCHER_PACKAGE) {
      return this.voucherPackageForButton()
    } else {
      throw new Error('Unhandled button type: ' + this.type)
    }
  }

  itemOrderItemForButton (options) {
    const { deliveryType, adminApproved = false } = options
    const restrictOutOfStock = this.inventoryStrategy?.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT

    let defaultItemVariation

    if (
      this.itemVariation &&
      this.itemVariation.active &&
      !this.itemVariation.salesDisabled &&
      (
        !restrictOutOfStock ||
        !this.$isOutOfStock({
          deliveryType,
          itemVariation: this.itemVariation
        })
      )
    ) {
      defaultItemVariation = this.itemVariation
    } else {
      defaultItemVariation = _(this.resolvedItem?.activeItemVariations).filter(itemVariation => {
        return !itemVariation.salesDisabled && (
          !restrictOutOfStock ||
          !this.$isOutOfStock({
            deliveryType,
            itemVariation
          })
        )
      }).first()
    }

    const outOfStockModifierIds = restrictOutOfStock
      ? _(this.resolvedItem?.itemModifierSets.data)
        .map('modifierSet.activeModifiers')
        .flatten()
        .filter(modifier => {
          return this.$isOutOfStock({
            deliveryType,
            item: this.resolvedItem,
            modifier,
            selectedItemVariationId: defaultItemVariation?.id
          })
        })
        .map('id')
        .value()
      : null

    const defaultItemModifiers = []
    _(this.resolvedItem?.itemModifierSets.data)
      .flatMap(modifierSet => modifierSet.defaultModifiers.data.map(modifier => ({ modifier, modifierSet })))
      .each(({ modifier, modifierSet }) => {
        if (!modifier.active) return

        if (modifier.salesDisabled) return

        if (restrictOutOfStock && _.includes(outOfStockModifierIds, modifier.id)) return

        defaultItemModifiers.push(new ItemOrderItemModifier({ modifierId: modifier.id, modifierSetId: modifierSet.modifierSetId }))
      })

    return new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: this.resolvedItem?.id,
        itemVariationId: defaultItemVariation?.id,
        itemModifiers: defaultItemModifiers,
        adminApproved
      },
      voucher: null,
      voucherType: null,
      discount: null
    })
  }

  itemPackageForButton () {
    return this.itemPackage
  }

  voucherPackageForButton () {
    return this.voucherPackage.voucherPackageOrderItem
  }

  voucherOrderItemForButton (serviceIssue) {
    const voucher = new Voucher({
      voucherTypeId: this.voucherTypeId,
      card: null,
      user: null
    })

    return new OrderItem({
      type: serviceIssue ? 'voucher-service-issue' : 'voucher-purchase',
      voucherPurchase: voucher,
      voucher: null,
      voucherType: null,
      discount: null
    })
  }

  giftCardOrderItemForButton () {
    const giftCard = new GiftCard({
      voucherType: this.voucherType,
      initialValue: 0,
      card: null
    })

    // TODO tax on voucher sale?
    return new OrderItem({
      type: 'gift-card',
      giftCard,
      voucher: null,
      voucherType: null,
      discount: null
    })
  }

  miscCashOutOrderItemForButton () {
    return new OrderItem({
      type: 'misc-item',
      description: 'Charge for Cash Withdrawal',
      amount: 0,
      voucher: null,
      voucherType: null,
      discount: null,
      isCashOut: true
    })
  }

  miscOrderItemForButton () {
    const orderItem = new OrderItem({
      type: 'misc-item',
      description: this.buttonOptions.default_description,
      amount: this.buttonOptions.default_amount,
      voucher: null,
      voucherType: null,
      discount: null,
      taxable: this.buttonOptions.default_taxable,
      taxIds: this.buttonOptions.default_tax_ids,
      print: this.buttonOptions.default_print,
      defaultItemSourceLocationId: this.buttonOptions.default_print_location_id,
      itemSourceLocationId: this.buttonOptions.default_print ? this.buttonOptions.default_print_location_id : null
    })

    return orderItem
  }

  donationOrderItemForButton () {
    return new OrderItem({
      type: 'donation',
      description: this.buttonOptions.default_description,
      amount: this.buttonOptions.default_amount,
      voucher: null,
      voucherType: null,
      discount: null,
      taxable: this.buttonOptions.default_taxable,
      details: [{ title: this.buttonOptions.button_name, donorPerfectCampaignCode: this.buttonOptions.donor_perfect_campaign_code }]
    })
  }

  userCreditOrderItemForButton () {
    return new OrderItem({
      type: 'user-credit',
      amount: 1000,
      voucher: null,
      voucherType: null,
      discount: null
    })
  }
}
